import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Close as CloseIcon } from '@material-ui/icons';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { useStyles as useDialogStyles } from '../styles';
import messages from './messages';

interface ApproveOpeningsDialogProps {
  /**
   * Named in the body, so the dialog says which row it came from. Read only
   * when one opening is selected — several cannot be named in a sentence.
   */
  openingName: string | null;
  /** How many openings this approval covers. */
  count: number;
  onCancel: () => void;
  onConfirm: () => void;
}

const TITLE_ID = 'approve-openings-title';

/**
 * The count, agreed to before a bulk approval is recorded (Design
 * Requirements §5).
 *
 * ## Why the bulk path confirms and a row's own Approve does not
 *
 * §5 decides both: a single opening's Approve "records the approval
 * immediately, with no confirmation dialog", and a bulk decision has "a
 * confirmation showing the count" in front of it. One press here decides every
 * selected opening at once, and nothing un-approves one — so the count is the
 * thing the reader agrees to, the way the bulk rejection's dialog already
 * makes them agree to one reason covering every row.
 *
 * ## Confirm closes it, unlike the rejection's
 *
 * `RejectOpeningDialog` outlives its confirm because it owns the reason field
 * and reports a refusal against it. This dialog owns nothing the run needs:
 * the bar's own running state is the in-flight UI, and a refusal lands in the
 * outcome summary — so staying open would only put a modal in front of both.
 *
 * ## The confirm carries primary weight, unlike its two siblings
 *
 * `DeleteRuleDialog` and `RejectOpeningDialog` outline their confirm in the
 * error role because each ends something. An approval is the queue's
 * progressive action — the same act the bar's own filled Approve offers — so
 * demoting it here would read as the dialog warning against the thing it
 * exists to complete. The chrome is otherwise theirs, from the same styles.
 */
export const ApproveOpeningsDialog: React.FC<ApproveOpeningsDialogProps> = ({
  openingName,
  count,
  onCancel,
  onConfirm,
}) => {
  const styles = useDialogStyles();
  const intl = useIntl();
  const theme = useTheme();
  // At 448px the dialog is wider than the smallest phones, and a modal that
  // overflows puts its actions off the edge.
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  return (
    <Dialog
      aria-labelledby={TITLE_ID}
      classes={{ paper: styles.dialogPaper }}
      fullScreen={fullScreen}
      onClose={onCancel}
      open
    >
      <DialogTitle className={styles.dialogTitle} disableTypography>
        <Typography id={TITLE_ID} variant="subtitle2">
          <FormattedMessage
            {...(count > 1
              ? messages.approveOpeningsTitle
              : messages.approveOpeningTitle)}
            values={{ count }}
          />
        </Typography>
        <IconButton
          aria-label={intl.formatMessage(globalMessages.close)}
          className={styles.dialogClose}
          onClick={onCancel}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent} dividers>
        {/* Which opening, or how many. The dialog is opened from the bar over
            many rows, and by the time it is up the rows are behind it — the
            sentence is what the reader agrees to. */}
        <Typography className={styles.dialogSentence} variant="body2">
          {count > 1 ? (
            <FormattedMessage
              {...messages.approveOpeningsBody}
              values={{ count }}
            />
          ) : (
            <FormattedMessage
              {...messages.approveOpeningBody}
              values={{ opening: openingName ?? '' }}
            />
          )}
        </Typography>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          className={styles.dialogButton}
          variant="outlined"
          onClick={onCancel}
        >
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        <Button
          className={styles.dialogConfirmButton}
          color="primary"
          variant="contained"
          onClick={onConfirm}
        >
          <FormattedMessage
            {...(count > 1
              ? messages.confirmApproveOpenings
              : messages.confirmApproveOpening)}
            values={{ count }}
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
